import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import { colors, fonts, zindex } from '../../styles/theme';
import DropdownMenuGroup from './DropdownMenuGroup';
import DropdownMenuItem from './DropdownMenuItem';
import customIcons from '../../styles/icons';
import { isTouchEnabled } from '../../utils/mobile';

export const DropdownMenu = styled('ul')`
  background: ${colors.white};
  box-shadow: 0 0 3px 0 ${colors.grey.altDark};
  border: 1px solid ${colors.white};
  border-radius: 0 0 2px 2px;
  display: block;
  margin: 0;
  padding: 0;
  max-height: 400px;
  overflow-x: hidden;
  overflow-y: auto;
  position: absolute;
  white-space: nowrap;
  z-index: ${zindex.component.base};
  text-align: left;
  min-width: 100%;
  &:before {
    display: ${({ title }) => (title ? 'block' : 'none')};
    content: attr(title);
    padding: 0 3px 3px 3px;
    margin: 7px 7px 5px 7px;
    font-size: ${fonts.sans.size.small};
    line-height: ${fonts.sans.size.xlarge};
    font-weight: ${fonts.sans.weight.bold};
    color: ${colors.blue.primaryDark};
    border-bottom: 1px solid ${colors.cs.secondary};
    text-transform: uppercase;
    pointer-events: none;
  }
`;

const DropdownMenuWrapper = styled('div')`
  position: relative;
`;

const disabledOverideStyle = css`
  &:hover {
    cursor: not-allowed;
  }
  > *:not(a) {
    background: ${colors.grey.secondary};
    color: ${colors.grey.altLight};
    border-color: ${colors.grey.secondary};
    &:hover {
      background: ${colors.grey.secondary};
      color: ${colors.grey.altLight};
      border-color: ${colors.grey.secondary};
      pointer-events: none;
      cursor: not-allowed;
    }
  }
  > a {
    color: ${colors.grey.secondaryDark};
    &:hover {
      text-decoration: none;
      pointer-events: none;
    }
  }
`;

const triggerArrowStyle = css`
  > *:not(${DropdownMenuWrapper}) {
    padding-right: 30px;
  }
  &::after {
    ${fonts.icons};
    content: '${customIcons['angle-down']}';
    font-size: 18px;
    position: absolute;
    top: 0;
    text-align: center;
    height: 100%;
    right: 0;
    box-sizing: border-box;
    width: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: inherit;
  }
`;

const fullWidthStyle = css`
  width: 100%;
  box-sizing: border-box;
  > *:not(${DropdownMenuWrapper}) {
    width: 100%;
  }
`;

const DropdownContainerWrapper = styled.div`
  display: inline-block;
  outline: 0;
  position: relative;
  ${(props) => props.fullWidth && fullWidthStyle};
  ${(props) => props.triggerArrow && triggerArrowStyle};
  ${(props) => props.disabled && disabledOverideStyle};
  margin-top: 10px;
  &::after {
    color: ${(props) => props.triggerArrowColor};
  }
  &:hover > * {
    cursor: pointer;
  }
`;

class Dropdown extends Component {
  constructor(props) {
    super(props);
    this.state = { isOpen: false };
  }

  toggleDropdown = () => {
    const { isOpen } = this.state;
    const { disabled } = this.props;

    if (!disabled) {
      this.setState({ isOpen: !isOpen });
    }
  };

  onClickFunc = (itemFunc) => (evt) => {
    this.toggleDropdown();
    itemFunc(evt);
  };

  renderLinks(links) {
    return links.map((link) => {
      const isLinkGroup = Array.isArray(link);
      // noop if group is empty
      if (isLinkGroup && !link.length) return <Fragment key="empty-group" />;

      if (isLinkGroup) {
        return (
          <DropdownMenuGroup key={link.map((l) => l.name).join()}>
            {this.renderLinks(link)}
          </DropdownMenuGroup>
        );
      }

      return (
        <DropdownMenuItem
          value={link.name}
          href={link.path}
          key={link.name}
          action={link.action ? this.onClickFunc(link.action) : null}
          disabled={link.disabled}
        />
      );
    });
  }

  renderMenu() {
    const { isOpen } = this.state;
    if (!isOpen) return null;
    const { links, className, title } = this.props;

    return (
      <DropdownMenuWrapper className={className}>
        <DropdownMenu title={title}>{this.renderLinks(links)}</DropdownMenu>
      </DropdownMenuWrapper>
    );
  }

  render() {
    const {
      Trigger,
      triggerProps,
      triggerArrow,
      triggerArrowColor,
       
      links,
      disabled,
      fullWidth,
      hoverEffects,
      ...otherProps
    } = this.props;

    return (
      <DropdownContainerWrapper
        onMouseEnter={() => hoverEffects && !isTouchEnabled() && this.toggleDropdown()}
        onMouseLeave={() => hoverEffects && !isTouchEnabled() && this.toggleDropdown()}
        onClick={() => this.toggleDropdown()}
        {...otherProps}
        role="menu"
        tabIndex="0"
        disabled={disabled}
        triggerArrow={triggerArrow}
        triggerArrowColor={triggerArrowColor}
        fullWidth={fullWidth}
      >
        <Trigger {...triggerProps} />
        {this.renderMenu()}
      </DropdownContainerWrapper>
    );
  }
}

const dropdownLinkShape = PropTypes.shape({
  value: PropTypes.string,
  href: PropTypes.string,
});

Dropdown.defaultProps = {
  triggerProps: {},
  triggerArrow: false,
  triggerArrowColor: null,
  className: '',
  disabled: false,
  title: '',
  fullWidth: null,
  hoverEffects: true,
};

Dropdown.propTypes = {
  className: PropTypes.string,
  Trigger: PropTypes.func.isRequired,
  triggerProps: PropTypes.shape({}),
  triggerArrow: PropTypes.bool,
  triggerArrowColor: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.oneOfType([dropdownLinkShape, PropTypes.arrayOf(dropdownLinkShape)])
  ).isRequired,
  disabled: PropTypes.bool,
  title: PropTypes.string,
  fullWidth: PropTypes.bool,
  hoverEffects: PropTypes.bool,
};

export default Dropdown;
